// src/features/system/admin/components/IAMManagement.tsx
//
// Umbrella component that groups IAM panels under sub-tabs:
//   • Tenants — tenant management (create, list, provision)
//   • Users   — full IAM user management (CRUD, roles, status)
//   • RBAC    — role / permission catalog + user→role mapping

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import UserManagementV2 from './UserManagementV2';
import RbacManagementV2 from './RbacManagementV2';
import TenantsTable from '@/features/system/tenants/components/TenantsTable';
import EmailTemplatesPreview from './EmailTemplatesPreview';
import IamAdminPanel from '@/features/system/iam-admin/components/IamAdminPanel';
import IdentityLinkingPanel from './IdentityLinkingPanel';
import FederatedProvisioningPanel from './FederatedProvisioningPanel';
import MercadoPagoAccountManagement from './MercadoPagoAccountManagement';
import { useAuth } from '@/shared/auth';

type IAMTab = 'tenants' | 'users' | 'provision' | 'rbac' | 'memberships' | 'identity' | 'mercadopago' | 'templates';

const TABS: { key: IAMTab; label: string }[] = [
  { key: 'tenants',     label: 'Tenants' },
  { key: 'users',       label: 'Users' },
  { key: 'provision',   label: 'Provision' },
  { key: 'rbac',        label: 'RBAC' },
  { key: 'memberships', label: 'Memberships' },
  { key: 'identity',    label: 'Identity Linking' },
  { key: 'mercadopago', label: 'Mercado Pago' },
  { key: 'templates',   label: 'Email Templates' },
];

const VALID_IAM_TABS = new Set<IAMTab>(['tenants', 'users', 'provision', 'rbac', 'memberships', 'identity', 'mercadopago', 'templates']);

export default function IAMManagement() {
  const { capabilities } = useAuth();
  const isSuperadmin = capabilities.isSuperadmin === true;
  const canManageIam = isSuperadmin || capabilities.canManageIam === true;

  const visibleTabs = useMemoTabs(canManageIam, isSuperadmin);

  // Read initial tab from URL (enables BubbleNav deep-linking on mobile)
  const [sp] = useSearchParams();
  const [active, setActive] = useState<IAMTab>(() => {
    const t = sp.get('iamTab') as IAMTab | null;
    return t && visibleTabs.some(x => x.key === t) ? t : 'users';
  });
  // Sync with URL when BubbleNav navigates while already mounted
  useEffect(() => {
    const t = sp.get('iamTab') as IAMTab | null;
    if (t && VALID_IAM_TABS.has(t) && t !== active) setActive(t);
  }, [sp]);

  useDocumentTitle('Admin', 'IAM', TABS.find(t => t.key === active)?.label);

  return (
    <div className="space-y-4">
      {/* Sub-tab bar — desktop only; BubbleNav handles mobile */}
      <div className="hidden md:flex gap-1 border-b border-neutral-800 overflow-x-auto">
        {visibleTabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            className={`px-4 py-2 text-sm whitespace-nowrap transition-colors ${
              active === t.key
                ? 'text-emerald-400 border-b-2 border-emerald-400 font-semibold pac-glass-subtle'
                : 'text-neutral-500 hover:text-neutral-300 hover:border-neutral-600/30'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Panel */}
      {active === 'tenants'     && <TenantsTable />}
      {active === 'users'       && <UserManagementV2 />}
      {active === 'provision'   && isSuperadmin && <FederatedProvisioningPanel />}
      {active === 'rbac'        && <RbacManagementV2 />}
      {active === 'memberships' && canManageIam && <IamAdminPanel />}
      {active === 'identity'    && <IdentityLinkingPanel />}
      {active === 'mercadopago' && <MercadoPagoAccountManagement />}
      {active === 'templates'   && <EmailTemplatesPreview />}
    </div>
  );
}

// Memoised, capability-aware tab list. Memberships + Mercado Pago account
// management require IAM admin capabilities; Provision is superadmin-only.
function useMemoTabs(canManageIam: boolean, isSuperadmin: boolean): { key: IAMTab; label: string }[] {
  return React.useMemo(
    () => TABS.filter(t => {
      if (t.key === 'memberships' || t.key === 'mercadopago') return canManageIam;
      if (t.key === 'provision') return isSuperadmin;
      return true;
    }),
    [canManageIam, isSuperadmin],
  );
}
